import { JsonObject } from '../../app/json/jsonValue.js';

/**
 * Read-only context handed to an IDataSource when resolving a declared capture.
 */
export class CaptureContext {
    /** The scenario's DynamicAction result (keyed by componentName_ActionType -> result object). */
    readonly ActionResult: JsonObject;
    /** The scenario's resolved config JSON (e.g. for an identifier the source needs). */
    readonly ConfigJson: JsonObject | null;
    /** The argument portion of the capture source (text after "scheme:"), e.g. "UserId". */
    readonly SourceArg: string;
    /** Absolute path to the AssemblerWeb directory (for sources that read test-only files). */
    readonly AssemblerWebDir: string;
    /** Values captured earlier in this scenario, so a later capture may reference one. */
    readonly CapturedValues: Map<string, string>;

    constructor(
        actionResult: JsonObject,
        configJson: JsonObject | null,
        sourceArg: string,
        assemblerWebDir: string,
        capturedValues: Map<string, string>
    ) {
        this.ActionResult = actionResult;
        this.ConfigJson = configJson;
        this.SourceArg = sourceArg;
        this.AssemblerWebDir = assemblerWebDir;
        this.CapturedValues = capturedValues;
    }
}

/**
 * Resolves a single declared capture value for ordered test scenarios from a named source scheme,
 * so the generic test orchestrator carries no app-specific (e.g. Security) capture logic.
 *
 * Registered the same way as IDataReloadable: a typed parameter map passed to IRegistry.registerDataSource,
 * held in RegistryStore keyed by scheme.
 */
export abstract class IDataSource {
    /** The source scheme this resolver handles (the prefix before ':' in a capture's source). */
    abstract get scheme(): string;

    /** Resolves the capture value from the supplied context, or null when nothing is available. */
    abstract resolve(context: CaptureContext): string | null;
}
